import { useState, type FormEvent } from 'react';
import { AddButton } from '../ui/AddButton';
import { PASSWORD_MIN, ROLES, SPEECH_LANGUAGES, SPEECH_LANGUAGE_LABELS, type ResetLink, type Role, type SessionInfo, type SessionUser, type SpeechLanguage, shownSpaceColor } from '@dpw/shared';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { API_BASE, api } from '../api';
import { MembersSheet, managesMembers } from '../ui/Members';
import { useChange, useEmailStatus, useHealth, usePrefs, useSessions, useSpaces, useUpdatePrefs, useUsers, useVoiceStatus } from '../lib/queries';
import { ErrorNote, Loading } from '../ui/bits';
import { ago, gbDate, gbDateTime } from '../lib/format';
import { useToast } from '../ui/toast';
import { useDecide } from '../lib/decide';
import { PushRow } from '../ui/PushRow';
import { usePhone } from '../lib/media';
import { Title, TopBar } from '../phone/kit';
import { EditSpaceSheet, SpaceSheet } from '../ui/SpaceForm';
import { ArchiveButton, ArchivedPanel, DeletedPanel } from '../ui/Archive';
import { Topics, useResearchChange } from './Knowledge';
import { useQuery } from '@tanstack/react-query';

const ROLE_LABEL: Record<Role, string> = { partner: 'Partner', director: 'Director', member: 'Member' };

export function SettingsPage({ me }: { me: SessionUser }) {
  const phone = usePhone();
  const toast = useToast();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const pw = useChange(() => api.auth.changePassword(cur, next));
  const submitPw = (e: FormEvent) => { e.preventDefault(); pw.mutate(undefined, { onSuccess: () => { setCur(''); setNext(''); toast('Password changed. Other devices were signed out.'); } }); };
  return (
    <>
      {/* On a phone: the frame's back link to More (Settings had no way back) and the large title. */}
      {phone ? <><TopBar back={{ to: '/more', label: 'More' }} /><Title title="Settings" sub={`${me.name} · ${ROLE_LABEL[me.role]}${me.isAdmin ? ', admin' : ''}`} /></>
        : <div className="hdr"><div className="htl"><h1>Settings</h1></div></div>}
      <div className="colsx">
        <div className="l">
          <div className="panel col gap12">
            <div className="sh"><b>You</b></div>
            <div className="bl" style={{ gridTemplateColumns: '120px 1fr' }}>
              <b>Name</b><span>{me.name}</span><b>Email</b><span>{me.email}</span><b>Role</b><span>{ROLE_LABEL[me.role]}{me.isAdmin ? ', admin' : ''}</span>
            </div>
          </div>
          <form className="panel col gap12" onSubmit={submitPw}>
            <div className="sh"><b>Change password</b></div>
            <div className="formrow">
              <label className="fld">Current password<input className="inp" type="password" autoComplete="current-password" value={cur} onChange={e => setCur(e.target.value)} required /></label>
              <label className="fld">New password ({PASSWORD_MIN}+ characters)<input className="inp" type="password" autoComplete="new-password" minLength={PASSWORD_MIN} value={next} onChange={e => setNext(e.target.value)} required /></label>
            </div>
            {pw.error ? <ErrorNote error={pw.error} /> : null}
            <div><button className="bt pri" disabled={pw.isPending}>Change password</button></div>
          </form>
          <Devices />
          {managesMembers(me) && <SpacesMembers me={me} />}
          {me.isAdmin && <Team />}
          {me.role !== 'partner' && <ArchivedPanel />}
          {me.role !== 'partner' && <DeletedPanel />}
        </div>
        <div className="r">
          {me.isAdmin && <Health />}
          {me.isAdmin && <div className="panel"><div className="sh"><b>Usage</b><span>Admins only</span></div><div className="xs mut" style={{ lineHeight: 1.6 }}>Time in the app by person and day, and what the paid services cost this month.</div><div style={{ marginTop: 8 }}><Link className="bt sm" to="/settings/usage">Open Usage</Link></div></div>}
          <MorningEmailPanel admin={me.isAdmin} />
          <EmailPanel admin={me.isAdmin} me={me} />
          <VoicePanel />
          {me.role !== 'partner' && <ResearchPanel admin={me.isAdmin} />}
          <div className="panel col gap12" role="region" aria-label="Notifications"><div className="sh"><b>Notifications</b><Link className="bt sm" to="/notifications#hear">What you hear about</Link></div>
            <div className="xs mut" style={{ lineHeight: 1.6 }}>You hear about activities you own, mentions, reminders for you, and questions sent to you. Never everything by default. Push comes to each browser where you turn it on, for the kinds you chose, outside your quiet hours.</div>
            <PushRow /></div>
        </div>
      </div>
    </>
  );
}

/**
 * Research (DECISIONS, Research Center build choices): the Research lead, one person an admin names, who edits the
 * research topics with the admins and accepts the topics the assistant proposes; and the topics themselves.
 */
function ResearchPanel({ admin }: { admin: boolean }) {
  const toast = useToast();
  const ov = useQuery({ queryKey: ['research'], queryFn: api.research.overview });
  const users = useUsers();
  const setLead = useResearchChange((id: string | null) => api.research.setLead(id));
  const lead = ov.data?.lead ?? null;
  return (
    <>
      <div className="panel col gap6" role="region" aria-label="Research lead">
        <div className="sh"><b>Research lead</b><span>{admin ? 'Admins set it' : 'Set by an admin'}</span></div>
        <div className="xs mut" style={{ lineHeight: 1.6 }}>One person who collects outside intelligence: edits the research topics with the admins and accepts topics the assistant proposes.</div>
        {admin ? (
          <select className="inp" aria-label="Research lead" value={lead?.id ?? ''} disabled={setLead.isPending}
            onChange={e => setLead.mutate(e.target.value || null, { onSuccess: p => toast(p ? `${p.name} is the Research lead.` : 'No Research lead now. Admins edit the topics.') })}>
            <option value="">Nobody</option>
            {(users.data ?? []).filter(u => u.role !== 'partner').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        ) : <div className="sm">{lead ? lead.name : 'Nobody yet'}</div>}
        {setLead.error ? <ErrorNote error={setLead.error} /> : null}
      </div>
      <Topics />
    </>
  );
}

/**
 * Settings, Spaces (SSettings): each space with its people, Edit, and Members to add anyone, change their role, remove
 * them; then the dashed New space card.
 */
function SpacesMembers({ me }: { me: SessionUser }) {
  const spaces = useSpaces();
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="panel col gap12" role="region" aria-label="Spaces">
      <div className="sh"><b>Spaces</b><span>The director and admins</span></div>
      {spaces.data && !spaces.data.length && <div className="xs mut">No spaces yet. Create the first one: a client engagement or an internal programme.</div>}
      <div className="spgrid">
        {(spaces.data ?? []).map(s => (
          <div className="spc" key={s.id} data-space={s.name}>
            <div className="rowc"><span className={`dt ${shownSpaceColor(s.color)}`} style={{ marginTop: 0 }} /><b className="sm">{s.name}</b></div>
            <div className="xs mut">{s.clientName ?? 'Internal'} · {s.tracks} {s.tracks === 1 ? 'track' : 'tracks'}</div>
            <div className="rowc"><Link className="bt sm" to={`/spaces/${s.id}`}>Open</Link><button type="button" className="bt sm" onClick={() => setEditing(s.id)} aria-label={`Edit ${s.name}`}>Edit</button>
              <button type="button" className="bt sm" onClick={() => setOpen(s.id)}>Members</button>
              <ArchiveButton kind="space" id={s.id} name={s.name} count={s.tracks} then="/settings" /></div>
          </div>
        ))}
        <div className="spc spnew"><b className="sm">New space</b><span className="xs mut">A client engagement or an internal programme.</span>
          <AddButton onClick={() => setCreating(true)}>New space</AddButton></div>
      </div>
      <AnimatePresence>{open && <MembersSheet key={open} spaceId={open} onClose={() => setOpen(null)} />}</AnimatePresence>
      <AnimatePresence>{editing && <EditSpaceSheet key={editing} me={me} spaceId={editing} onClose={() => setEditing(null)} />}</AnimatePresence>
      <AnimatePresence>{creating && <SpaceSheet key="new" me={me} onClose={() => setCreating(false)} />}</AnimatePresence>
    </div>
  );
}

function Team() {
  const users = useUsers();
  const toast = useToast();
  const [f, setF] = useState({ name: '', email: '', role: 'member' as Role, isAdmin: false, password: '' });
  const add = useChange(() => api.users.create(f));
  // The link a new person was sent (or, with email not connected, the one to share by hand).
  const [invite, setInvite] = useState<{ name: string; link: ResetLink } | null>(null);
  const update = useChange((x: { id: string; isActive: boolean }) => api.users.update(x.id, { isActive: x.isActive }));
  return (
    <>
      <form className="panel col gap12" onSubmit={e => { e.preventDefault(); add.mutate(undefined, { onSuccess: u => {
        setInvite(u.invite ? { name: u.name, link: u.invite } : null);
        toast(u.invite ? (u.invite.emailedTo ? `Added ${u.name}. Emailed them a link to set their password.` : `Added ${u.name}. Email is not connected: copy their link below.`)
          : `Added ${u.name}. Share the password with them privately.`);
        setF({ name: '', email: '', role: 'member', isAdmin: false, password: '' });
      } }); }}>
        <div className="sh"><b>Add a person</b><span>Admins only</span></div>
        <div className="formgrid">
          <label className="fld">Full name<input className="inp" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required /></label>
          <label className="fld">Work email<input className="inp" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} required /></label>
          <label className="fld">Role<select className="inp" value={f.role} onChange={e => setF({ ...f, role: e.target.value as Role })}>{ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select></label>
          <label className="fld">First password (optional)<input className="inp" type="text" minLength={PASSWORD_MIN} value={f.password} onChange={e => setF({ ...f, password: e.target.value })} placeholder="Leave empty to email them a link" /></label>
        </div>
        <label className="togrow"><span className="cbx"><input type="checkbox" checked={f.isAdmin} onChange={e => setF({ ...f, isAdmin: e.target.checked })} /><i aria-hidden="true" /></span>Admin (sees every space, manages people)</label>
        <div className="xs mut">Leave the password empty and they get an email with a link to set their own (it works for 7 days).</div>
        {add.error ? <ErrorNote error={add.error} /> : null}
        {invite && (
          <div className="col gap6" role="status">
            <div className="xs">{invite.link.emailedTo ? `Emailed ${invite.name} at ${invite.link.emailedTo}. Their link, if they need it again:` : `Email is not connected. Send ${invite.name} this link privately:`}</div>
            <div className="rowc gap6">
              <input className="inp" readOnly value={invite.link.url} aria-label={`Password link for ${invite.name}`} onFocus={e => e.target.select()} style={{ flex: 1 }} />
              <button type="button" className="bt sm" onClick={() => { void navigator.clipboard?.writeText(invite.link.url).then(() => toast('Link copied')); }}>Copy link</button>
            </div>
          </div>
        )}
        <div><button className="bt pri" disabled={add.isPending}>Add person</button></div>
      </form>
      <div className="panel">
        <div className="sh" style={{ marginBottom: 6 }}><b>People</b><span>{users.data?.length ?? 0}</span></div>
        {(users.data ?? []).map(u => (
          <div className="lrow" key={u.id} style={{ flexWrap: 'wrap' }}>
            <span className="who2">{u.initials}</span>
            <div style={{ flex: 1, minWidth: 0 }}><div className="sm">{u.name}</div><div className="xs mut">{u.email} · {ROLE_LABEL[u.role]}{u.isAdmin ? ', admin' : ''}</div></div>
            {u.isActive && <ResetButton userId={u.id} name={u.name} />}
            <button className="bt sm" onClick={() => update.mutate({ id: u.id, isActive: !u.isActive })}>{u.isActive ? 'Deactivate' : 'Reactivate'}</button>
          </div>
        ))}
        {update.error ? <ErrorNote error={update.error} /> : null}
      </div>
    </>
  );
}

function device(s: SessionInfo) {
  const ua = s.userAgent ?? '';
  const what = s.client === 'app' ? 'iPhone app'
    : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  return [what, os].filter(Boolean).join(' on ');
}

/** Where you are signed in, with a way to sign a device out. */
function Devices() {
  const q = useSessions();
  const revoke = useChange((id: string) => api.auth.revokeSession(id));
  return (
    <div className="panel">
      <div className="sh" style={{ marginBottom: 6 }}><b>Where you are signed in</b><span>{q.data?.length ?? 0}</span></div>
      {q.isLoading ? <Loading /> : (q.data ?? []).map(s => (
        <div className="lrow" key={s.id}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sm">{device(s)}{s.current ? <span className="xs mut"> · this device</span> : null}</div>
            <div className="xs mut">Last used {ago(s.lastSeenAt)}{s.ip ? ` · ${s.ip}` : ''} · signs out by itself {gbDate(new Date(s.expiresAt), { day: 'numeric', month: 'short' })}</div>
          </div>
          {!s.current && <button className="bt sm" onClick={() => revoke.mutate(s.id)} disabled={revoke.isPending} aria-label={`Sign out ${device(s)}`}>Sign out</button>}
        </div>
      ))}
      <div className="xs mut" style={{ marginTop: 6 }}>Browser sessions end after 7 days unused (30 at most); the app after 30 days unused (90 at most).</div>
      {revoke.error ? <ErrorNote error={revoke.error} /> : null}
    </div>
  );
}

/** Is the background worker running? Reminders and team checks stop moving when it is not. */
function Health() {
  const h = useHealth();
  const w = h.data?.worker;
  return (
    <div className="panel">
      <div className="sh"><b>System</b></div>
      {!h.data ? <Loading /> : (
        <div className="xs" style={{ lineHeight: 1.6 }}>
          <span className={`pl ${w?.stale ? 'red' : 'grn'}`}>{w?.stale ? 'Worker not running' : 'Worker running'}</span>{' '}
          <span className="mut">{w?.lastBeatAt ? `last heartbeat ${ago(w.lastBeatAt)}` : 'no heartbeat yet'}</span>
          {w?.stale ? <div className="mut">Reminders and team checks are not moving. Check the dpw-worker service on the server.</div> : null}
        </div>
      )}
    </div>
  );
}

/** Voice and transcription (SSettings, Connections): ElevenLabs. Record, the Speak buttons and talking need it. */
function VoicePanel() {
  const v = useVoiceStatus().data;
  const prefs = usePrefs().data;
  const update = useUpdatePrefs();
  return (
    <div className="panel col gap6" role="region" aria-label="Voice">
      <div className="sh"><b>Voice and transcription</b>{v && <span className={`pl ${v.transcribe ? 'grn' : 'amb'}`}>{v.transcribe ? 'On' : 'Not connected'}</span>}</div>
      <div className="xs mut" style={{ lineHeight: 1.6 }}>ElevenLabs. Record meetings, speak instead of typing, listen.
        {v && !v.transcribe ? ' Not connected on this server: an admin adds the ElevenLabs key to the server settings (README, Voice). Until then Record is off; logging meetings by hand works.' : ''}</div>
      {/* What the voice service listens for, for you (Speak, Talk, the meetings you record). The assistant answers in the language of your words. */}
      <label className="fld">I speak in
        <select className="inp" aria-label="I speak in" value={prefs?.voice.language ?? 'eng'} disabled={!prefs || update.isPending}
          onChange={e => update.mutate({ voice: { language: e.target.value as SpeechLanguage } })}>
          {SPEECH_LANGUAGES.map(l => <option key={l} value={l}>{SPEECH_LANGUAGE_LABELS[l]}</option>)}
        </select>
      </label>
      {update.error ? <ErrorNote error={update.error} /> : null}
    </div>
  );
}

/**
 * Email logging (SSettings, Connections): the inbound address people forward or copy email to, and whether the app
 * sends (Amazon SES). Then the emails the assistant could not place, each with why, Log it (opens it in Requests to
 * pick the space and track) and Dismiss: yours, and everyone's for the director. Admins also see what failed for good
 * lately and why, for example the SES sandbox refusing an address it has not verified; the "Email not sent"
 * notifications link here.
 */
function EmailPanel({ admin, me }: { admin: boolean; me: SessionUser }) {
  const q = useEmailStatus(true);
  const s = q.data;
  const decide = useDecide();
  return (
    <div className="panel col gap6" id="email" role="region" aria-label="Email">
      <div className="sh"><b>Email logging</b>{s && <span className={`pl ${s.receiving && s.sending ? 'grn' : 'amb'}`}>{s.receiving && s.sending ? 'On' : s.receiving || s.sending ? 'Partly on' : 'Not connected'}</span>}</div>
      {!s ? (q.error ? <ErrorNote error={q.error} /> : <Loading />) : (
        <>
          <div className="xs mut" style={{ lineHeight: 1.6 }}>
            <span className="mono inaddr">{s.inboundAddress}</span> receives forwards and copies{s.receiving ? '' : ' (not connected on this server yet)'}; replies come from the app (Amazon SES){s.sending ? '' : ', not connected on this server yet'}.
          </div>
          <b className="xs" style={{ marginTop: 6 }}>Not logged yet</b>
          <div className="col gap6" aria-label="Emails not logged" role="list">
            {!s.unplaced.length ? <div className="xs mut" role="listitem">Every email that came in is logged, waiting for approval, or dismissed.</div> : s.unplaced.map(u => {
              const other = u.owner.id !== me.id;
              return (
                <div className="emprob" role="listitem" key={u.actionId} data-unplaced={u.actionId}>
                  <div className="rowc gap6"><span className="pl amb">Not logged</span><b className="xs" style={{ flex: 1, minWidth: 0 }}>{u.subject}</b><span className="xs mut">{ago(u.at)}</span></div>
                  <div className="xs mut">{u.from ? `From ${u.from}. ` : ''}{u.reason}{other ? ` For ${u.owner.name}.` : ''}</div>
                  <div className="rowc gap6">
                    <Link className="bt pri sm" to={`/requests?id=${u.actionId}${other ? '&for=everyone' : ''}`} aria-label={`Log it: ${u.subject}`}>Log it</Link>
                    <button type="button" className="bt sm" disabled={decide.busy(u.actionId)} aria-label={`Dismiss: ${u.subject}`}
                      onClick={() => decide.ignore({ id: u.actionId, kind: 'log_email', status: 'waiting', version: 1, title: u.subject })}>Dismiss</button>
                  </div>
                </div>
              );
            })}
          </div>
          {admin && (
            <div className="col gap6" aria-label="Email problems" role="list">
              {!s.problems.length ? <div className="xs mut" role="listitem">Nothing failed in the last 14 days.</div> : s.problems.map((p, i) => (
                <div className="emprob" role="listitem" key={i}>
                  <div className="rowc gap6"><span className="pl red">{p.direction === 'out' ? 'Not sent' : 'Not read'}</span><b className="xs" style={{ flex: 1, minWidth: 0 }}>{p.subject}</b><span className="xs mut">{ago(p.at)}</span></div>
                  <div className="xs mut">{p.direction === 'out' ? `To ${p.who}` : `From ${p.who || 'an unknown sender'}`}: {p.error}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Morning email (6.3): your switch (on unless you turn it off). Admins also see who has it on and when theirs last
 * went, open a preview of their own in a new tab, and can send themselves one now.
 */
function MorningEmailPanel({ admin }: { admin: boolean }) {
  const q = useQuery({ queryKey: ['morning-email'], queryFn: api.morningEmail.status });
  const prefs = usePrefs().data;
  const update = useUpdatePrefs();
  const toast = useToast();
  const send = useChange(() => api.morningEmail.sendMe());
  const s = q.data;
  const on = prefs?.morningEmail ?? s?.on ?? true;
  return (
    <div className="panel col gap6" id="morning-email" role="region" aria-label="Morning email">
      <div className="sh"><b>Morning email</b>{s && <span className={`pl ${s.sending ? (on ? 'grn' : '') : 'amb'}`}>{!s.sending ? 'Email not connected' : on ? 'On' : 'Off'}</span>}</div>
      <div className="xs mut" style={{ lineHeight: 1.6 }}>A short email at 07:00 Riyadh time, Sunday to Thursday: prep at risk, what is due today, what waits for your approval, and new insights. Nothing is sent on a day with nothing in it.
        {s && !s.sending ? ' Email is not connected on this server yet, so nothing is sent.' : ''}</div>
      {/* The whole row is the switch (44px to the touch on a phone). */}
      <button type="button" className="togrow mswitch" role="switch" aria-checked={on} aria-label="Morning email"
        onClick={() => update.mutate({ morningEmail: !on }, { onSuccess: () => { void q.refetch(); toast(!on ? 'Morning email on.' : 'Morning email off.'); } })}>
        <span className={`tog${on ? ' on' : ''}`} aria-hidden="true" /><span>Send me the morning email</span>
      </button>
      {update.error ? <ErrorNote error={update.error} /> : null}
      {admin && (
        <>
          <div className="rowc gap6" style={{ flexWrap: 'wrap', marginTop: 4 }}>
            <a className="bt sm" href={`${API_BASE}/api/morning-email/preview`} target="_blank" rel="noopener">Preview my morning email</a>
            <button type="button" className="bt pri sm" disabled={send.isPending || !s?.sending}
              onClick={() => send.mutate(undefined, { onSuccess: r => toast(`Sent to you: ${r.subject}`) })}>Send me one now</button>
          </div>
          {send.error ? <ErrorNote error={send.error} /> : null}
          <b className="xs" style={{ marginTop: 6 }}>Who has it on</b>
          <div className="col" role="list" aria-label="Who has the morning email on">
            {(s?.people ?? []).map(p => (
              <div className="lrow" role="listitem" key={p.id} data-person={p.name}>
                <span className="sm" style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                <span className="xs mut">{p.lastSent ? `last sent ${ago(p.lastSent)}` : 'not sent yet'}</span>
                <span className={`pl ${p.on ? 'grn' : ''}`}>{p.on ? 'On' : 'Off'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Admin: a one-time link the person opens to set a new password (24 hours). Share it privately. */
function ResetButton({ userId, name }: { userId: string; name: string }) {
  const [link, setLink] = useState<ResetLink | null>(null);
  const make = useChange(() => api.users.resetPassword(userId));
  const toast = useToast();
  if (link) {
    return (
      <div className="col gap6" style={{ flexBasis: '100%' }}>
        <input className="inp" readOnly value={link.url} aria-label={`Password link for ${name}`} onFocus={e => e.target.select()} />
        <div className="rowc gap6">
          <button className="bt sm" onClick={() => { void navigator.clipboard?.writeText(link.url).then(() => toast('Link copied')); }}>Copy link</button>
          <span className="xs mut" role="status">{link.emailedTo ? `Emailed to ${link.emailedTo}. ` : 'Email is not connected: share it privately. '}Works once, until {gbDateTime(new Date(link.expiresAt), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. Using it signs {name.split(' ')[0]} out everywhere.</span>
        </div>
      </div>
    );
  }
  return (
    <>
      <button className="bt sm" onClick={() => make.mutate(undefined, { onSuccess: setLink })} disabled={make.isPending}>Reset password</button>
      {make.error ? <ErrorNote error={make.error} /> : null}
    </>
  );
}
